import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserById, updateUser, fetchCountriesForDropdown, fetchStatesByCountry } from '../services/api.ts';
import { User } from '../types/user.ts';
import { ArrowLeft, User as UserIcon, Save, Loader } from 'lucide-react';
import './EditUser.css';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface State {
  id: number;
  name: string;
  code: string;
}

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    postcode: 0,
    state: '',
    state_id: 0,
    country: '',
    country_id: 0,
    device_name: '',
    is_active: false,
    ads: false,
    can_order: false,
    test_account: false
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data: User = await fetchUserById(id);
        setFormData({
          first_name: data.first_name || '',
          middle_name: data.middle_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          postcode: data.postcode || 0,
          state: data.state || '',
          state_id: data.state_id || 0,
          country: data.country || '',
          country_id: data.country_id || 0,
          device_name: data.device_name || '',
          is_active: data.is_active || false,
          ads: data.ads || false,
          can_order: data.can_order || false,
          test_account: data.test_account || false
        });
        
        // Load states if country_id exists
        if (data.country_id) {
          const statesData = await fetchStatesByCountry(data.country_id);
          setStates(statesData);
        }
        
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  const handleCountryChange = async (countryId: number) => {
    const selectedCountry = countries.find(c => c.id === countryId);
    setFormData({ 
      ...formData, 
      country_id: countryId,
      country: selectedCountry?.name || '',
      state: '',
      state_id: 0 // Reset state_id when country changes
    });
    
    if (countryId) {
      try {
        const statesData = await fetchStatesByCountry(countryId);
        setStates(statesData);
      } catch (err) {
        console.error('Error loading states:', err);
        setStates([]);
      }
    } else {
      setStates([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      setSaving(true);
      await updateUser(id, {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        postcode: formData.postcode,
        state: formData.state,
        country: formData.country,
        country_id: formData.country_id,
        state_id: formData.state_id,
        device_name: formData.device_name,
        is_active: formData.is_active,
        ads: formData.ads,
        can_order: formData.can_order,
        test_account: formData.test_account
      });
      navigate('/dashboard/users');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update user';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-user-page">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading user...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-page">
      <div className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/dashboard/users')} title="Back to Users">
            <ArrowLeft size={20} />
          </button>
          <div className="header-title">
            <h2>Edit User</h2>
            <p className="subtitle">{formData.first_name} {formData.last_name}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <div className="form-card">
            <div className="card-header">
              <UserIcon size={20} />
              <h3>Personal Information</h3>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label>Middle Name</label>
                <input
                  type="text"
                  value={formData.middle_name}
                  onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                  placeholder="Enter middle name"
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Enter last name"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <label>Device Name</label>
                <input
                  type="text"
                  value={formData.device_name}
                  onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                  placeholder="Device name"
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="form-card">
            <div className="card-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <h3>Location</h3>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Country</label>
                <select
                  value={formData.country_id}
                  onChange={(e) => handleCountryChange(parseInt(e.target.value))}
                >
                  <option value={0}>Select a country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>State</label>
                <select
                  value={formData.state_id}
                  onChange={(e) => {
                    const selectedStateId = parseInt(e.target.value);
                    const selectedState = states.find(s => s.id === selectedStateId);
                    setFormData({ 
                      ...formData, 
                      state_id: selectedStateId,
                      state: selectedState?.name || '' 
                    });
                  }}
                  disabled={states.length === 0}
                >
                  <option value={0}>Select a state</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Postcode</label>
                <input
                  type="number"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: parseInt(e.target.value) || 0 })}
                  placeholder="Enter postcode"
                />
              </div>
            </div>
          </div>

          {/* Status Settings */}
          <div className="form-card">
            <div className="card-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              <h3>Settings</h3>
            </div>
            
            <div className="settings-grid">
              <div className="setting-item">
                <div className="setting-info">
                  <label>Active Status</label>
                  <p>Enable or disable this user account</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Ads Enabled</label>
                  <p>Show advertisements to this user</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.ads}
                    onChange={(e) => setFormData({ ...formData, ads: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Can Order</label>
                  <p>Allow this user to place orders</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.can_order}
                    onChange={(e) => setFormData({ ...formData, can_order: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Test Account</label>
                  <p>Mark this account as a test account</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.test_account}
                    onChange={(e) => setFormData({ ...formData, test_account: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={() => navigate('/dashboard/users')}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
